import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { QuickAccessService } from '../../core/services/quick-access.service';

@Component({
  selector: 'app-default-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './default-sidebar.component.html',
  styleUrl: './default-sidebar.component.scss',
})
export class DefaultSidebarComponent implements OnInit {
  currentUrl  = '';
  userRole    = '';
  userName    = '';
  userInitial = 'U';
  openGroup: string | null = null;

  ctxMenu: { visible: boolean; x: number; y: number; route: string } =
    { visible: false, x: 0, y: 0, route: '' };

  private readonly GROUP_ROUTES: Record<string, string[]> = {
    library:       ['/add-book', '/books', '/authors', '/categories', '/languages', '/book-inventory', '/book-locator', '/search-by-author', '/search-by-category', '/book-catalogue'],
    operations:    ['/issue-receive', '/barcode-labels', '/book-claims', '/inter-center-transfer'],
    management:    ['/add-member', '/members', '/users', '/routes', '/centers', '/closure-reasons'],
    configuration: ['/settings', '/label-config'],
    reports:       ['/reports', '/daily-summary', '/reports/receipt-payment', '/reports/closed-members'],
  };

  // Change password modal
  showCpModal = false;
  cpCurrent = '';
  cpNew = '';
  cpConfirm = '';
  showCpCurrent = false;
  showCpNew = false;
  showCpConfirm = false;
  cpSaving = false;
  cpError = '';
  cpSuccess = '';

  get cpHas8(): boolean      { return this.cpNew.length >= 8; }
  get cpHasUpper(): boolean  { return /[A-Z]/.test(this.cpNew); }
  get cpHasLower(): boolean  { return /[a-z]/.test(this.cpNew); }
  get cpHasNumber(): boolean { return /[0-9]/.test(this.cpNew); }
  get cpHasSpecial(): boolean { return /[@$!%*?&#^()_+\-=]/.test(this.cpNew); }
  get cpIsValid(): boolean {
    return !!this.cpCurrent && this.cpHas8 && this.cpHasUpper && this.cpHasLower &&
           this.cpHasNumber && this.cpHasSpecial && this.cpNew === this.cpConfirm;
  }

  openCpModal(): void {
    this.cpCurrent = ''; this.cpNew = ''; this.cpConfirm = '';
    this.showCpCurrent = false; this.showCpNew = false; this.showCpConfirm = false;
    this.cpSaving = false; this.cpError = ''; this.cpSuccess = '';
    this.showCpModal = true;
  }

  closeCpModal(): void { this.showCpModal = false; }

  submitCpModal(): void {
    if (!this.cpIsValid || this.cpSaving) return;
    this.cpSaving = true;
    this.cpError = '';
    this.cpSuccess = '';
    const userId = this.auth.getCurrentUser()?.userID;
    this.api.changePassword(userId, this.cpCurrent, this.cpNew).subscribe({
      next: () => {
        this.cpSaving = false;
        this.cpSuccess = 'Password changed successfully!';
        setTimeout(() => this.closeCpModal(), 1500);
      },
      error: (err: any) => {
        this.cpSaving = false;
        this.cpError = err?.error?.message || 'Failed to change password.';
      },
    });
  }

  constructor(private router: Router, private auth: AuthService, public qa: QuickAccessService, private api: ApiService) {}

  ngOnInit(): void {
    const user        = this.auth.getCurrentUser();
    this.userRole     = user?.role  || '';
    this.userName     = user?.name  || 'User';
    this.userInitial  = (user?.name || 'U').charAt(0).toUpperCase();

    this.currentUrl = this.router.url;
    this.syncOpenGroup();

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.currentUrl = e.urlAfterRedirects;
        this.syncOpenGroup();
      });
  }

  // Open the group that owns the current route; clear when on a non-group page.
  private syncOpenGroup(): void {
    const active = ['library', 'operations', 'management', 'configuration', 'reports']
      .find(g => this.isGroupHasActive(g));
    this.openGroup = active ?? null;
  }

  isAdmin(): boolean          { return this.userRole?.toLowerCase() === 'admin'; }
  isManager(): boolean        { return this.userRole?.toLowerCase() === 'manager'; }
  isManagerOrAdmin(): boolean { const r = this.userRole?.toLowerCase(); return r === 'admin' || r === 'manager'; }

  isRouteActive(path: string): boolean { return this.currentUrl.startsWith(path); }

  isGroupHasActive(group: string): boolean {
    const routes = [...(this.GROUP_ROUTES[group] || [])];
    // Manager sees Members and Add Member inside Operations
    if (group === 'operations' && this.isManager()) routes.push('/members', '/add-member', '/inter-center-transfer');
    return routes.some(r => this.currentUrl.startsWith(r));
  }

  toggleGroup(group: string): void {
    this.openGroup = this.openGroup === group ? null : group;
  }

  goToPage(route: string): void { this.router.navigateByUrl(route); }

  onMenuRightClick(event: MouseEvent, route: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.ctxMenu = { visible: true, x: event.clientX, y: event.clientY, route };
  }

  pinRoute(route: string): void {
    this.qa.pin(route);
    this.ctxMenu.visible = false;
  }

  unpinRoute(route: string): void {
    this.qa.unpin(route);
    this.ctxMenu.visible = false;
  }

  @HostListener('document:click')
  closeCtxMenu(): void { this.ctxMenu.visible = false; }

  logout(): void {
    if (confirm('Are you sure you want to log out?')) {
      this.auth.logout();
    }
  }
}
